import { Fragment, useEffect, useState } from "react";

import { SettingsRow } from "./parts";
import styles from "./Settings.module.css";
import { Switch } from "../../components/Switch";
import {
  type NotificationChannel,
  useNotificationPreferences,
} from "../../data/api/notificationPreferences";
import {
  currentSubscription,
  disablePush,
  enablePush,
  fetchVapidPublicKey,
  isPushSupported,
} from "../../push/webPush";

// The per-kind rows of the preferences matrix, in reading order (grouped by
// what they're about). Labels are user-facing; `kind` is the server key.
//
// One row per kind in notifications.md §Kinds, and nothing else. A row for a
// kind nothing produces is a switch that promises an alert which never
// arrives — and the promise is only ever discovered by not being told about
// something, which is the failure this whole surface exists to prevent.
const KIND_ROWS: ReadonlyArray<{ kind: string; label: string }> = [
  { kind: "task_needs_input", label: "Needs your input" },
  { kind: "task_failed", label: "Task failed" },
  { kind: "pr_opened", label: "PR opened" },
  { kind: "pr_open_failed", label: "PR could not be opened" },
  { kind: "pr_merged", label: "PR merged" },
  { kind: "build_status_failed", label: "Build failed" },
  { kind: "build_status_action_required", label: "Build needs action" },
];

const CHANNELS: ReadonlyArray<{ channel: NotificationChannel; label: string }> =
  [
    { channel: "in_app", label: "In-app" },
    { channel: "email", label: "Email" },
    { channel: "web_push", label: "Push" },
  ];

export const Notifications = () => {
  const { preferenceFor, setPreference } = useNotificationPreferences();
  const supported = isPushSupported();
  // null = "still checking / not configured server-side"; a string enables the
  // toggle. Push is off unless the server advertises a VAPID key.
  const [vapidKey, setVapidKey] = useState<string | null>(null);
  const [pushOn, setPushOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!supported) {
      return;
    }
    let active = true;
    void (async () => {
      const [key, sub] = await Promise.all([
        fetchVapidPublicKey().catch(() => null),
        currentSubscription().catch(() => null),
      ]);
      if (!active) {
        return;
      }
      setVapidKey(key);
      setPushOn(sub !== null);
    })();
    return () => {
      active = false;
    };
  }, [supported]);

  const configured = supported && vapidKey !== null;

  const togglePush = (next: boolean): void => {
    if (busy || !vapidKey) {
      return;
    }
    setBusy(true);
    setError(null);
    const action = next ? enablePush(vapidKey) : disablePush();
    void action
      .then(() => setPushOn(next))
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : "Something went wrong."),
      )
      .finally(() => setBusy(false));
  };

  const pushHelp = configured
    ? "Get push notifications on this device, even when the dashboard is closed."
    : supported
      ? "Push is not configured on this server."
      : "This browser does not support web push.";

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Notifications</h3>
      <div className={styles.card}>
        <SettingsRow help={pushHelp} label="Web push">
          <Switch
            aria-label="Web push"
            checked={pushOn}
            disabled={!configured || busy}
            onCheckedChange={togglePush}
          />
        </SettingsRow>
        {error ? (
          <div className={styles.help} style={{ color: "var(--needs-input)" }}>
            {error}
          </div>
        ) : null}
        <div className={styles.prefGrid}>
          <div className={styles.prefHead} style={{ textAlign: "left" }}>
            Per notification
          </div>
          {CHANNELS.map((c) => (
            <div className={styles.prefHead} key={c.channel}>
              {c.label}
            </div>
          ))}
          {KIND_ROWS.map((row) => {
            const prefs = preferenceFor(row.kind);
            return (
              <Fragment key={row.kind}>
                <div className={styles.prefKind}>{row.label}</div>
                {CHANNELS.map((c) => (
                  <div className={styles.prefCell} key={c.channel}>
                    <Switch
                      aria-label={`${row.label} — ${c.label}`}
                      checked={prefs[c.channel]}
                      onCheckedChange={(v) =>
                        setPreference(row.kind, c.channel, v)
                      }
                    />
                  </div>
                ))}
              </Fragment>
            );
          })}
        </div>
      </div>
    </section>
  );
};
